import { type ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { BrewDetail, RecipeDetail } from "../api";
import { type BrewLog, brewLogOf, LEAST_ROWS, sheetDate, sheetSize, tableRows } from "../brewSheet";
import { today } from "../format";
import {
  brewingFacts,
  ingredientGroups,
  lineFacts,
  lineName,
  lineOrigin,
  RECIPE_TYPE_LABELS,
  showFahrenheit,
  showGravity,
} from "../recipeFormat";

/**
 * What a recipe prints, from its page or a brew's (Scott's Jam, 2026-10-08): what hangs on each tank's
 * clipboard at White Oak, a brew log "with the recipe sheet attached" (Tom, 2026-09-04). Two pages: the
 * Brew Log filled in, then the recipe. They are drawn only for the printer, beside the app rather than in
 * it, so Print, or the browser's own print, gives them in place of the screen.
 */
export function PrintSheets({
  recipe,
  brew = null,
}: {
  recipe: RecipeDetail | undefined;
  brew?: BrewDetail | null;
}) {
  // The day it is printed, as it is printed, for a page left open overnight.
  const [printedOn, setPrintedOn] = useState(today);
  useEffect(() => {
    const now = () => setPrintedOn(today());
    window.addEventListener("beforeprint", now);
    return () => window.removeEventListener("beforeprint", now);
  }, []);
  if (!recipe) return null;
  const foot = (
    <footer className="sheet-foot">
      <span>
        {recipe.name}, version {recipe.version}
        {brew?.number != null ? `, brew ${brew.number}` : ""}
      </span>
      <span>Printed {sheetDate(printedOn)} from BreweryLogistics</span>
    </footer>
  );
  return createPortal(
    <div className="print-sheets">
      <BrewLogPage log={brewLogOf(recipe, brew)} foot={foot} />
      <RecipePage recipe={recipe} foot={foot} />
    </div>,
    document.body,
  );
}

function BrewLogPage({ log, foot }: { log: BrewLog; foot: ReactNode }) {
  const { compact, cellarRows } = sheetSize(log);
  // A table's lines, then blank rows to write in.
  const rows = <T,>(lines: T[], least: number, spare = !compact): (T | null)[] => [
    ...lines,
    ...Array<null>(tableRows(lines.length, least, spare) - lines.length).fill(null),
  ];
  return (
    <section className={`sheet-page brew-log ${compact ? "compact" : ""}`}>
      <h1 className="sheet-title">Brew Log</h1>
      <div className="sheet-head">
        <span>
          Brew: <span className="sheet-blank wide">{log.name}</span>
        </span>
        <span>
          Brew Date: <span className="sheet-blank">{log.brewDate}</span>
        </span>
        <span>
          Brew #: <span className="sheet-blank short">{log.brewNumber}</span>
        </span>
        <span>
          Tank: <span className="sheet-blank short">{log.tank}</span>
        </span>
      </div>
      <p className="sheet-about">{log.about}</p>

      <div className="sheet-columns">
        <div className="sheet-fields">
          {log.fields.map((run, i) => (
            // Runs in the day's order, each on from the last: their labels are their keys.
            // biome-ignore lint/suspicious/noArrayIndexKey: the runs are fixed, mash, boil, knockout
            <div key={i} className="sheet-run">
              {run.map((f) => (
                <div key={f.label} className="sheet-field">
                  <span className="sheet-label">{f.label}</span>
                  <span className="sheet-line">{f.value}</span>
                  <span className="sheet-aim">{f.aim ? `recipe ${f.aim}` : ""}</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="sheet-tables">
          <p className="sheet-targets">
            Target OG: <b>{log.targetOg}</b> FG: <b>{log.targetFg || " ".repeat(6)}</b>
          </p>
          <SheetTable
            heads={["Additive", "Quantity"]}
            widths={["", LAST_COLUMN]}
            rows={rows(log.additives, LEAST_ROWS.additives).map((a) =>
              a ? [a.name, a.amount] : null,
            )}
          />
          <SheetTable
            heads={["Grist", "#'s"]}
            widths={["", LAST_COLUMN]}
            rows={rows(log.grist, LEAST_ROWS.grist).map((g) => (g ? [g.name, g.pounds] : null))}
          />
          <SheetTable
            heads={["Hops", "#'s", "Time"]}
            widths={["", "16%", LAST_COLUMN]}
            numeric={[1]}
            rows={rows(log.hops, LEAST_ROWS.hops).map((h) =>
              h ? [h.name, h.pounds, h.time] : null,
            )}
          />
          <SheetTable
            heads={["Yeast", "Gen"]}
            widths={["", LAST_COLUMN]}
            rows={rows(log.yeast, LEAST_ROWS.yeast, false).map((y) => (y ? [y.name, y.gen] : null))}
          />
        </div>
      </div>

      <SheetTable
        heads={["Date", "Gravity", "Temp", "pH"]}
        widths={["25%", "25%", "25%", "25%"]}
        rows={Array<null>(cellarRows).fill(null)}
      />
      <SheetTable
        heads={["Dry Hop Date", "Crash Date", "Fine Date", "Rack Date", "BBLs", "Volume CO2"]}
        rows={[["", "", "", "", "", log.carbonation ? `recipe ${log.carbonation}` : ""]]}
        aims={[5]}
      />
      <div className="sheet-notes">
        <strong>Notes</strong>
        {log.notes ? <p>{log.notes}</p> : null}
        <div className="sheet-rule" />
        <div className="sheet-rule" />
        <div className="sheet-rule" />
      </div>
      {foot}
    </section>
  );
}

/**
 * The width of the last column of each table beside the blanks, Quantity, #'s, Time and Gen, so their
 * edges and their words line up down the page (Tyler, 2026-10-19). A hop's #'s sits between, set to
 * the right, since hops have three columns.
 */
const LAST_COLUMN = "34%";

/** One of the paper's gray tables: a row of heads, then a row for each, null for a blank one. */
function SheetTable({
  heads,
  widths = [],
  numeric = [],
  aims = [],
  rows,
}: {
  heads: string[];
  widths?: string[];
  /** Columns of figures, set to the right. */
  numeric?: number[];
  /** Columns holding the recipe's figure, printed small. */
  aims?: number[];
  rows: (string[] | null)[];
}) {
  const cls = (i: number) =>
    [numeric.includes(i) ? "num" : "", aims.includes(i) ? "sheet-aim" : ""]
      .filter(Boolean)
      .join(" ") || undefined;
  return (
    <table className="sheet-table">
      <thead>
        <tr>
          {heads.map((h, i) => (
            <th key={h} style={widths[i] ? { width: widths[i] } : undefined} className={cls(i)}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, r) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: rows are printed once, never reordered
          <tr key={r}>
            {heads.map((h, i) => (
              <td key={h} className={cls(i)}>
                {row?.[i] ?? ""}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The recipe as its page shows it, without stock, links or buttons, and a box to tick for each line. */
function RecipePage({ recipe: r, foot }: { recipe: RecipeDetail; foot: ReactNode }) {
  const e = r.estimates;
  const pills = [
    r.style
      ? `${r.style.name}${r.style.code ? ` ${r.style.code}` : ""}${r.style.guide ? `, ${r.style.guide}` : ""}`
      : null,
    RECIPE_TYPE_LABELS[r.type] ?? r.type,
    `Version ${r.version}`,
  ].filter(Boolean);
  const meta = [
    r.brewer ? `By ${r.brewer}` : null,
    r.writtenOn ? `Written ${sheetDate(r.writtenOn)}` : null,
    r.importedFrom ? `Imported from ${r.importedFrom}` : null,
  ].filter(Boolean);
  const figures: [string, string][] = [
    ["Original gravity", showGravity(e.og)],
    ["Final gravity", showGravity(e.fg)],
    ["ABV", e.abv === null ? "-" : `${e.abv.toFixed(1)}%`],
    ["Bitterness", `${e.ibu.toFixed(1)} IBU`],
    ["Color", `${e.srm.toFixed(1)} SRM`],
  ];
  return (
    <section className="sheet-page recipe-sheet">
      <p className="sheet-pills">{pills.join(" · ")}</p>
      <h1 className="sheet-name">{r.name}</h1>
      {meta.length ? <p className="sheet-meta">{meta.join(" · ")}</p> : null}
      {r.description ? <p className="sheet-meta">{r.description}</p> : null}
      <div className="sheet-figures">
        {figures.map(([label, value]) => (
          <div key={label}>
            <div className="sheet-figure-label">{label}</div>
            <div className="sheet-figure num">{value}</div>
          </div>
        ))}
      </div>

      <h2 className="sheet-heading">Ingredients</h2>
      <table className="sheet-list">
        <thead>
          <tr>
            <th className="sheet-tick" aria-label="Added" />
            <th className="num">Amount</th>
            <th>Ingredient</th>
            <th>Details</th>
            <th className="num">Share</th>
          </tr>
        </thead>
        {ingredientGroups(r.lines).map((g) => (
          <tbody key={g.title}>
            <tr className="sheet-group">
              <th colSpan={5}>{g.title}</th>
            </tr>
            {g.lines.map((l) => {
              const { amount, details, share } = lineFacts(l, "lb");
              return (
                <tr key={l.id}>
                  <td className="sheet-tick">
                    <span className="sheet-box" />
                  </td>
                  <td className="num">{amount}</td>
                  <td>
                    {lineName(l)}
                    {lineOrigin(l) ? <span className="sheet-faint"> ({lineOrigin(l)})</span> : null}
                  </td>
                  <td>{details}</td>
                  <td className="num">{share}</td>
                </tr>
              );
            })}
          </tbody>
        ))}
      </table>

      {r.mashSteps.length ? (
        <>
          <h2 className="sheet-heading">Mash</h2>
          <table className="sheet-list">
            <thead>
              <tr>
                <th>Step</th>
                <th className="num">Temperature</th>
                <th className="num">Time</th>
              </tr>
            </thead>
            <tbody>
              {r.mashSteps.map((step) => (
                <tr key={`${step.name}-${step.tempC}-${step.minutes}`}>
                  <td>{step.name}</td>
                  <td className="num">{showFahrenheit(step.tempC)}</td>
                  <td className="num">{step.minutes} min</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}

      <h2 className="sheet-heading">Brewing</h2>
      <dl className="sheet-facts">
        {brewingFacts(r).map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd className="num">{value}</dd>
          </div>
        ))}
      </dl>

      {r.notes || r.tasteNotes ? (
        <>
          <h2 className="sheet-heading">Notes</h2>
          {r.notes ? <p className="sheet-text">{r.notes}</p> : null}
          {r.tasteNotes ? (
            <p className="sheet-text">
              <strong>Tasting:</strong> {r.tasteNotes}
            </p>
          ) : null}
        </>
      ) : null}
      {foot}
    </section>
  );
}
